package dk.smark.demo.location

import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow

private external class GeoCoordinates {
    val latitude: Double
    val longitude: Double
    val accuracy: Double
}

private external class GeoPosition {
    val coords: GeoCoordinates
}

private external class GeoError {
    val code: Short
    val message: String
}

private external interface GeoOptions {
    var enableHighAccuracy: Boolean?
    var timeout: Int?
    var maximumAge: Int?
}

private external class Geolocation {
    fun getCurrentPosition(
        successCallback: (GeoPosition) -> Unit,
        errorCallback: (GeoError) -> Unit = definedExternally,
        options: GeoOptions = definedExternally
    )

    fun watchPosition(
        successCallback: (GeoPosition) -> Unit,
        errorCallback: (GeoError) -> Unit = definedExternally,
        options: GeoOptions = definedExternally
    ): Int

    fun clearWatch(watchId: Int)
}

private external class Navigator {
    val geolocation: Geolocation?
}

private external class BrowserWindow {
    val navigator: Navigator
}

@Suppress("UnsafeCastFromDynamic")
private fun browserWindow(): BrowserWindow =
    js("window").unsafeCast<BrowserWindow>()

@Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
private fun geoOptions(): GeoOptions =
    js("({})").unsafeCast<GeoOptions>().apply {
        enableHighAccuracy = true
        timeout = 15000
        maximumAge = 0
    }

private class BrowserLocationService : LocationService {
    override suspend fun getCurrentLocation(): LocationResult {
        val geolocation = browserWindow().navigator.geolocation
            ?: return LocationResult.Error(LocationError.LocationUnavailable)

        return suspendCoroutine { continuation ->
            geolocation.getCurrentPosition(
                successCallback = { position ->
                    continuation.resume(
                        LocationResult.Success(
                            GenericLocation(
                                latitude = position.coords.latitude,
                                longitude = position.coords.longitude,
                                accuracyMeters = position.coords.accuracy
                            )
                        )
                    )
                },
                errorCallback = { error ->
                    val mapped = when (error.code.toInt()) {
                        1 -> LocationError.PermissionDenied
                        2 -> LocationError.LocationUnavailable
                        3 -> LocationError.Timeout
                        else -> LocationError.Unknown(error.message)
                    }
                    continuation.resume(LocationResult.Error(mapped))
                },
                options = geoOptions()
            )
        }
    }

    override fun trackLocation(): Flow<LocationResult> = callbackFlow {
        val geolocation = browserWindow().navigator.geolocation
        if (geolocation == null) {
            trySend(LocationResult.Error(LocationError.LocationUnavailable))
            close()
            return@callbackFlow
        }

        val watchId = geolocation.watchPosition(
            successCallback = { position ->
                trySend(
                    LocationResult.Success(
                        GenericLocation(
                            latitude = position.coords.latitude,
                            longitude = position.coords.longitude,
                            accuracyMeters = position.coords.accuracy
                        )
                    )
                )
            },
            errorCallback = { error ->
                val mapped = when (error.code.toInt()) {
                    1 -> LocationError.PermissionDenied
                    2 -> LocationError.LocationUnavailable
                    3 -> LocationError.Timeout
                    else -> LocationError.Unknown(error.message)
                }
                trySend(LocationResult.Error(mapped))
            },
            options = geoOptions()
        )

        awaitClose { geolocation.clearWatch(watchId) }
    }
}

actual fun createLocationService(): LocationService = BrowserLocationService()